import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Collapse,
  Divider,
  CircularProgress,
  Fade,
  Tooltip,
  TextField,
  Alert
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useQueryClient } from '@tanstack/react-query';
import { useScriptByIdQuery } from '../hooks/useScripts';
import { useScriptBuilder } from '../hooks/useScriptBuilder';
import { useItemAutoSave } from '../hooks/useItemAutoSave';
import {
  NO_CATALOG_BACKING_LABEL,
  noCatalogBackingHint,
  isOverrideRescuable
} from '../utils/datasheetPreviewHints';
import { StepList } from './StepList';
import type { Script, TestPointPreview } from '../types';
import type { ItemScriptData, TestPointRef } from '../types/steps';
import type { SaveStatus } from '../hooks/useItemAutoSave';
import type { StepClipboard } from './TestPointsSection';

type BuilderApi = ReturnType<typeof useScriptBuilder>;

const formatNumber = (value: number): string =>
  Number(value.toFixed(10)).toString();

const ToleranceSection = ({
  builder,
  preview
}: {
  builder: BuilderApi;
  preview?: TestPointPreview;
}) => {
  const ref = builder.testPointRef;
  const [draft, setDraft] = useState(
    ref?.toleranceOverride != null ? String(ref.toleranceOverride) : ''
  );

  const commit = useCallback(
    (value: string) => {
      if (!ref) return;
      const trimmed = value.trim();
      if (trimmed === '') {
        if (ref.toleranceOverride == null) return;
        const next: TestPointRef = { ...ref };
        delete next.toleranceOverride;
        builder.setTestPointRef(next);
        return;
      }
      const parsed = Number(trimmed);
      if (!Number.isFinite(parsed) || parsed <= 0) return;
      if (parsed === ref.toleranceOverride) return;
      builder.setTestPointRef({ ...ref, toleranceOverride: parsed });
    },
    [ref, builder]
  );

  if (!ref) return null;

  const resolved = preview?.resolved ?? null;
  const derivation = resolved?.derivation ?? null;
  const blockedReason = preview?.blockedReason ?? null;
  const hasOverride = ref.toleranceOverride != null;
  const showNoBacking =
    (resolved?.noCatalogBacking ?? false) || (hasOverride && !derivation);

  return (
    <Box
      sx={{
        mb: 1.5,
        p: 1.5,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'action.hover'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="overline" color="text.secondary">
          Tolerance
        </Typography>
        {resolved && (
          <Chip
            size="small"
            variant="outlined"
            color={resolved.toleranceSource === 'manual' ? 'warning' : 'default'}
            label={
              resolved.toleranceSource === 'manual'
                ? 'Manual override'
                : 'From catalog'
            }
          />
        )}
        {showNoBacking && (
          <Tooltip title={noCatalogBackingHint()}>
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              icon={<WarningAmberIcon />}
              label={NO_CATALOG_BACKING_LABEL}
            />
          </Tooltip>
        )}
      </Box>

      {resolved && (
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          Effective tolerance: ±{formatNumber(resolved.tolerance)} {ref.unit}
        </Typography>
      )}

      {derivation && (
        <Typography variant="caption" color="text.secondary" display="block">
          Catalog: |{formatNumber(ref.nominal)}|
          {derivation.accuracyPct != null
            ? ` × ${formatNumber(derivation.accuracyPct)}%`
            : ''}
          {derivation.accuracyCounts != null
            ? ` + ${formatNumber(derivation.accuracyCounts)} × ${formatNumber(derivation.resolution)}`
            : ''}{' '}
          = ±{formatNumber(derivation.catalogTolerance)} {ref.unit} (range{' '}
          {derivation.rangeId})
        </Typography>
      )}

      {!resolved && blockedReason && (
        <Alert severity="warning" sx={{ my: 1, py: 0 }}>
          {blockedReason === 'STUB_ITEM'
            ? 'Unresolved STUB — resolve the stub in the Builder. A manual tolerance will not rescue it.'
            : `No catalog tolerance (${blockedReason}). Enter an absolute tolerance below to make this point runnable.`}
        </Alert>
      )}

      <TextField
        label={`Tolerance override (${ref.unit})`}
        size="small"
        type="number"
        value={draft}
        placeholder={
          derivation ? formatNumber(derivation.catalogTolerance) : 'e.g. 0.05'
        }
        onChange={e => setDraft(e.target.value)}
        onBlur={() => commit(draft)}
        disabled={blockedReason != null && !isOverrideRescuable(blockedReason)}
        helperText="Absolute value in the point's unit. Leave blank to use the catalog tolerance."
        inputProps={{ inputMode: 'decimal', min: 0, step: 'any' }}
        sx={{ mt: 1, width: 260 }}
      />
    </Box>
  );
};

type Props = {
  item: Script;
  index: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onRemove: () => void;
  onSaveStatusChange?: (itemId: string, status: SaveStatus) => void;
  onDuplicate?: () => void;
  onCopy?: () => void;
  clipboard?: StepClipboard;
  onPaste?: () => void;
  preview?: TestPointPreview;
  parentScriptId?: string;
};

const parseItemSummary = (
  scriptData: string | null
): {
  label: string;
  functionId: string;
  nominal: number;
  unit: string;
  frequency?: number;
  beforeCount: number;
  calibrationCount: number;
  afterCount: number;
} | null => {
  if (!scriptData) return null;
  try {
    const parsed = JSON.parse(scriptData) as ItemScriptData;
    if (!parsed.testPointRef) return null;
    const ref = parsed.testPointRef;
    return {
      label: ref.label,
      functionId: ref.functionId,
      nominal: ref.nominal,
      unit: ref.unit,
      frequency: ref.frequency,
      beforeCount: (parsed.beforeSteps ?? []).length,
      calibrationCount: (parsed.calibrationSteps ?? []).length,
      afterCount: (parsed.afterSteps ?? []).length
    };
  } catch {
    return null;
  }
};

const ExpandedContentInner = ({
  item,
  itemScript,
  onSaveStatusChange,
  preview,
  parentScriptId
}: {
  item: Script;
  itemScript: Script;
  onSaveStatusChange?: (status: SaveStatus) => void;
  preview?: TestPointPreview;
  parentScriptId?: string;
}) => {
  const queryClient = useQueryClient();
  const builder = useScriptBuilder(itemScript.scriptData);
  const { saveStatus } = useItemAutoSave({
    scriptId: item.id,
    isDirty: builder.isDirty,
    getScriptData: builder.getScriptData,
    markClean: builder.markClean,
    activeScript: itemScript,
    parentScriptId: item.parentScriptId
  });

  const previewScriptId = parentScriptId ?? item.parentScriptId ?? null;
  useEffect(() => {
    if (saveStatus === 'saved' && previewScriptId) {
      queryClient.invalidateQueries({
        queryKey: ['datasheetPreview', previewScriptId]
      });
    }
  }, [saveStatus, previewScriptId, queryClient]);

  useEffect(() => {
    console.log(
      `[EXPANDED] mount scriptId=${item.id} parentScriptId=${item.parentScriptId}`,
      {
        before: builder.beforeSteps.length,
        cal: builder.calibrationSteps.length,
        after: builder.afterSteps.length,
        isDirty: builder.isDirty
      }
    );
    return () => {
      console.log(`[EXPANDED] unmount scriptId=${item.id}`);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    onSaveStatusChange?.(saveStatus);
  }, [saveStatus, onSaveStatusChange]);

  return (
    <Box sx={{ px: 2, pt: 1.5, pb: 2 }}>
      {builder.testPointRef && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Chip
            label={builder.testPointRef.label}
            color="primary"
            variant="outlined"
            size="small"
          />
          <Typography variant="caption" color="text.secondary">
            {builder.testPointRef.functionId.toUpperCase()} &mdash;{' '}
            {builder.testPointRef.nominal} {builder.testPointRef.unit}
            {builder.testPointRef.frequency
              ? ` @ ${builder.testPointRef.frequency} Hz`
              : ''}
          </Typography>
        </Box>
      )}

      <ToleranceSection builder={builder} preview={preview} />

      <Divider textAlign="left" sx={{ mb: 1.5 }}>
        <Typography variant="overline" color="text.secondary">
          Before Steps
        </Typography>
      </Divider>
      <StepList
        steps={builder.beforeSteps}
        testPointRef={builder.testPointRef}
        onAddStep={type => builder.addStep(type, 'beforeSteps')}
        onRemoveStep={id => builder.removeStep(id, 'beforeSteps')}
        onUpdateStep={(id, updates) =>
          builder.updateStep(id, updates, 'beforeSteps')
        }
        onReorder={(activeId, overId) =>
          builder.reorderSteps(activeId, overId, 'beforeSteps')
        }
      />

      <Divider textAlign="left" sx={{ my: 1.5 }}>
        <Typography variant="overline" color="text.secondary">
          Calibration Steps
        </Typography>
      </Divider>
      <StepList
        steps={builder.calibrationSteps}
        testPointRef={builder.testPointRef}
        onAddStep={type => builder.addStep(type, 'calibrationSteps')}
        onRemoveStep={id => builder.removeStep(id, 'calibrationSteps')}
        onUpdateStep={(id, updates) =>
          builder.updateStep(id, updates, 'calibrationSteps')
        }
        onReorder={(activeId, overId) =>
          builder.reorderSteps(activeId, overId, 'calibrationSteps')
        }
      />

      <Divider textAlign="left" sx={{ my: 1.5 }}>
        <Typography variant="overline" color="text.secondary">
          After Steps
        </Typography>
      </Divider>
      <StepList
        steps={builder.afterSteps}
        testPointRef={builder.testPointRef}
        onAddStep={type => builder.addStep(type, 'afterSteps')}
        onRemoveStep={id => builder.removeStep(id, 'afterSteps')}
        onUpdateStep={(id, updates) =>
          builder.updateStep(id, updates, 'afterSteps')
        }
        onReorder={(activeId, overId) =>
          builder.reorderSteps(activeId, overId, 'afterSteps')
        }
      />
    </Box>
  );
};

const ExpandedContent = ({
  item,
  onSaveStatusChange,
  preview,
  parentScriptId
}: {
  item: Script;
  onSaveStatusChange?: (status: SaveStatus) => void;
  preview?: TestPointPreview;
  parentScriptId?: string;
}) => {
  const { data: itemScript, isLoading } = useScriptByIdQuery(item.id);

  if (isLoading || !itemScript) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  return (
    <ExpandedContentInner
      item={item}
      itemScript={itemScript}
      onSaveStatusChange={onSaveStatusChange}
      preview={preview}
      parentScriptId={parentScriptId}
    />
  );
};

export const TestPointRow = ({
  item,
  index,
  isExpanded,
  onToggleExpand,
  onRemove,
  onSaveStatusChange,
  onDuplicate,
  onCopy,
  clipboard,
  onPaste,
  preview,
  parentScriptId
}: Props) => {
  const [itemSaveStatus, setItemSaveStatus] = useState<SaveStatus>('idle');

  const summary = useMemo(
    () => parseItemSummary(item.scriptData),
    [item.scriptData]
  );

  const canPaste =
    !!clipboard && clipboard.sourceScriptId !== item.id && !!onPaste;

  useEffect(() => {
    onSaveStatusChange?.(item.id, itemSaveStatus);
  }, [itemSaveStatus, item.id, onSaveStatusChange]);

  return (
    <Box
      sx={{
        border: 1,
        borderColor: isExpanded ? 'primary.main' : 'divider',
        borderRadius: 1,
        mb: 1,
        bgcolor: 'background.paper',
        overflow: 'hidden'
      }}
    >
      {/* Collapsed header row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1,
          py: 0.75,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' }
        }}
        onClick={onToggleExpand}
      >
        <Chip label={index + 1} size="small" sx={{ minWidth: 28, mr: 1 }} />

        <Typography
          variant="body2"
          fontWeight={500}
          noWrap
          sx={{ flex: 1, mr: 1 }}
        >
          {summary
            ? `${summary.functionId.toUpperCase()} — ${summary.nominal} ${summary.unit}${summary.frequency ? ` @ ${summary.frequency} Hz` : ''}`
            : item.globalId}
        </Typography>

        {summary && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mr: 1, whiteSpace: 'nowrap' }}
          >
            {summary.beforeCount} before, {summary.calibrationCount} cal,{' '}
            {summary.afterCount} after
          </Typography>
        )}

        <Fade in={itemSaveStatus === 'saving'} unmountOnExit>
          <CloudSyncIcon
            fontSize="small"
            sx={{ color: 'text.secondary', mr: 0.5 }}
          />
        </Fade>
        <Fade in={itemSaveStatus === 'saved'} unmountOnExit>
          <CheckCircleOutlineIcon
            fontSize="small"
            sx={{ color: 'success.main', mr: 0.5 }}
          />
        </Fade>
        <Fade in={itemSaveStatus === 'error'} unmountOnExit>
          <ErrorOutlineIcon
            fontSize="small"
            sx={{ color: 'error.main', mr: 0.5 }}
          />
        </Fade>

        {onDuplicate && (
          <Tooltip title="Duplicate test point">
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation();
                onDuplicate();
              }}
              sx={{
                color: 'text.disabled',
                '&:hover': { color: 'primary.main' },
                mr: 0.25
              }}
            >
              <FileCopyOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {onCopy && (
          <Tooltip title="Copy steps">
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation();
                onCopy();
              }}
              sx={{
                color: 'text.disabled',
                '&:hover': { color: 'primary.main' },
                mr: 0.25
              }}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {canPaste && (
          <Tooltip title="Paste steps">
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation();
                onPaste!();
              }}
              sx={{
                color: 'text.disabled',
                '&:hover': { color: 'success.main' },
                mr: 0.25
              }}
            >
              <ContentPasteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <IconButton
          size="small"
          onClick={e => {
            e.stopPropagation();
            onRemove();
          }}
          sx={{
            color: 'text.disabled',
            '&:hover': { color: 'error.main' },
            mr: 0.5
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <IconButton size="small">
          {isExpanded ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </IconButton>
      </Box>

      {/* Expanded content */}
      <Collapse in={isExpanded} mountOnEnter unmountOnExit>
        <Divider />
        <ExpandedContent
          item={item}
          onSaveStatusChange={setItemSaveStatus}
          preview={preview}
          parentScriptId={parentScriptId}
        />
      </Collapse>
    </Box>
  );
};
